import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ClearIcon from "@mui/icons-material/Clear";
import { styled } from "@mui/material/styles";
import {
  createPhotoGallery,
  resetPhotoGalleryState,
  updatePhotoGallery,
} from "../../redux/slices/photoGallerySlice";
import { useDispatch } from "react-redux";
import Notification from "../../components/common/Notification";
import { useAppSelector } from "../../redux/hooks";
import Loader from "../../components/common/Loader";
import { getAllEventCategories } from "../../redux/slices/eventCategorySlice";
import { getAllPressCategories } from "../../redux/slices/pressCategorySlice";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const StyledFormContainer = styled(Box)({
  width: "100%",
  height: "100%",
  backgroundColor: "background.default",
});

const StyledFormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  height: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const StyledTypography = styled(Typography)({
  fontFamily: "Poppins, sans-serif !important",
});

const GalleryImageContainer = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 16,
  marginTop: 16,
  marginBottom: 16,
  justifyContent: "flex-start",
});

const GalleryImageBox = styled(Box)({
  position: "relative",
  display: "inline-block",
});

const GalleryImage = styled("img")({
  width: 150,
  height: 150,
  objectFit: "cover",
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  border: "1px solid #eee",
  background: "#fafafa",
});

const Form = ({ onClose, editData }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    categoryId: "",
    categoryType: "eventCategory",
    status: "active",
    images: [],
    previews: [],
    altTags: "",
    seoTitle: "",
    seoDescription: "",
    seoKeywords: "",
  });

  const { loading, error, success } = useAppSelector(
    (state) => state.photoGallery
  );
  const [notification, setNotification] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (success) {
      setNotification({
        type: "success",
        message: editData
          ? "Field updated successfully!"
          : "Field created successfully!",
      });
      setTimeout(() => {
        setNotification(null);
        dispatch(resetPhotoGalleryState());
        onClose && onClose(true);
      }, 2000);
    }
  }, [success, editData, dispatch, onClose]);

  useEffect(() => {
    if (error) {
      setNotification({
        type: "error",
        message:
          typeof error === "string"
            ? error
            : error?.message || "Something went wrong!",
      });
    }
  }, [error]);

  useEffect(() => {
    if (editData) {
      setFormData((prev) => ({
        ...prev,
        name: editData.name || "",
        slug: editData.slug || "",
        categoryId: editData.categoryId?._id || editData.categoryId || "",
        categoryType: editData.categoryType || "eventCategory",
        status: editData.status || "active",
        images: [],
        previews: Array.isArray(editData.images)
          ? editData.images
          : editData.image
          ? [editData.image]
          : [],
        altTags: editData.altTags || "",
        description: editData.description || "",
        seoTitle: editData.seoTitle || "",
        seoDescription: editData.seoDescription || "",
        seoKeywords: editData.seoKeywords || "",
      }));
    }
  }, [editData]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        if (formData.categoryType === "eventCategory") {
          const result = await dispatch(
            getAllEventCategories({
              status: "active",
              page: 1,
              limit: 100,
            })
          ).unwrap();
          setCategories(result.data || []);
        } else if (formData.categoryType === "pressCategory") {
          const result = await dispatch(
            getAllPressCategories({
              status: "active",
              page: 1,
              limit: 100,
            })
          ).unwrap();
          setCategories(result.data || []);
        }
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        setNotification({
          type: "error",
          message: "Failed to load categories",
        });
      }
    };

    fetchCategories();
  }, [dispatch, formData.categoryType]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      // Auto-generate slug when name changes
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      setFormData((prev) => ({
        ...prev,
        name: value,
        slug: slug,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Update the handleSubmit function to correctly map SEO fields
  const handleSubmit = async (e) => {
    e.preventDefault();

    // First add validation for required fields
    if (!formData.name.trim()) {
      setNotification({
        type: "error",
        message: "Name is required",
      });
      return;
    }

    // Validate categoryId - THIS IS MISSING FROM YOUR SUBMISSION
    if (!formData.categoryId) {
      setNotification({
        type: "error",
        message: "Category is required",
      });
      return;
    }

    // Validate image for new entries
    if (!editData && !formData.images.length) {
      setNotification({
        type: "error",
        message: "At least one image is required",
      });
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append("name", formData.name.trim());
    formDataObj.append("slug", formData.slug);
    formDataObj.append("categoryId", formData.categoryId);
    formDataObj.append("categoryType", formData.categoryType);
    formDataObj.append("status", formData.status);
    formDataObj.append("altTags", formData.altTags);
    formDataObj.append("description", formData.description || "");
    formDataObj.append("seoTitle", formData.seoTitle || "");
    formDataObj.append("seoDescription", formData.seoDescription || "");
    formDataObj.append("seoKeywords", formData.seoKeywords || "");

    // Append all images
    formData.images.forEach((img, idx) => {
      formDataObj.append("images", img);
    });

    try {
      if (editData) {
        await dispatch(
          updatePhotoGallery({
            id: editData._id,
            data: formDataObj,
          })
        ).unwrap();
      } else {
        await dispatch(createPhotoGallery(formDataObj)).unwrap();
      }
      onClose && onClose(true);
    } catch (err) {
      setNotification({
        type: "error",
        message: err.message || "Something went wrong!",
      });
    }
  };

  return (
    <StyledFormContainer className="poppins-font">
      {loading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            background: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader />
        </Box>
      )}

      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: 1100,
        }}
        className="poppins-font"
      >
        <IconButton
          onClick={onClose}
          sx={{
            mr: 2,
            bgcolor: "background.paper",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <StyledTypography variant="h5" component="h1" fontWeight="bold">
          {editData ? "Edit" : "Add New"} Photo
        </StyledTypography>
      </Box>

      {/* Main Form Content */}
      <Box sx={{ p: 3 }}>
        <form onSubmit={handleSubmit} className="poppins-font">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* First Row: Profile Image + Basic Info */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 3,
              }}
            >
              {/* Profile Image - Takes 40% width */}
              <StyledFormSection sx={{ flex: 4 }}>
                <Typography variant="h6" className="poppins-font" gutterBottom>
                  Upload Images
                </Typography>
                {/* Upload Button */}
                <Box sx={{ textAlign: "center" }}>
                  <Button
                    component="label"
                    variant="contained"
                    size="large"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 2 }}
                  >
                    Upload Images
                    <VisuallyHiddenInput
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        if (!files.length) return;
                        let validFiles = [];
                        let previews = [];
                        let pending = files.length;
                        files.forEach((file) => {
                          if (file.size < 1024 || file.size > 2 * 1024 * 1024) {
                            setNotification({
                              type: "error",
                              message: "Each image must be between 1KB and 2MB",
                            });
                            pending--;
                            return;
                          }
                          // Remove dimension check
                          const src = URL.createObjectURL(file);
                          validFiles.push(file);
                          previews.push(src);
                          pending--;
                          if (pending === 0) {
                            setFormData((prev) => ({
                              ...prev,
                              images: [...prev.images, ...validFiles],
                              previews: [...prev.previews, ...previews],
                            }));
                          }
                        });
                      }}
                    />
                  </Button>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    (Each image must be between 1KB-2MB)
                  </Typography>
                </Box>
                {/* Gallery Images Preview */}
                <GalleryImageContainer>
                  {formData.previews.map((src, idx) => (
                    <GalleryImageBox key={idx}>
                      <GalleryImage src={src} alt={`Gallery ${idx + 1}`} />
                      <IconButton
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            images: prev.images.filter((_, i) => i !== idx),
                            previews: prev.previews.filter((_, i) => i !== idx),
                          }));
                        }}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "background.paper",
                          "&:hover": { backgroundColor: "background.default" },
                          boxShadow: 1,
                          zIndex: 2,
                        }}
                      >
                        <ClearIcon color="error" />
                      </IconButton>
                    </GalleryImageBox>
                  ))}
                </GalleryImageContainer>
              </StyledFormSection>

              {/* Basic Information - Takes 60% width */}
              <StyledFormSection sx={{ flex: 6 }}>
                <Typography
                  variant="h6"
                  className="poppins-font"
                  gutterBottom
                  sx={{ mb: 3 }}
                >
                  Basic Information
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Name *"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="poppins-font"
                  />

                  <TextField
                    fullWidth
                    label="Slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    disabled
                    helperText="Auto-generated from name"
                    className="poppins-font"
                  />

                  <FormControl fullWidth required>
                    <InputLabel className="poppins-font">
                      Category Type *
                    </InputLabel>
                    <Select
                      name="categoryType"
                      value={formData.categoryType}
                      label="Category Type *"
                      onChange={handleChange}
                      className="poppins-font"
                    >
                      <MenuItem value="eventCategory">Event Category</MenuItem>
                      <MenuItem value="pressCategory">Press Category</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth required sx={{ mt: 2 }}>
                    <InputLabel className="poppins-font">Category *</InputLabel>
                    <Select
                      name="categoryId"
                      value={formData.categoryId}
                      label="Category *"
                      onChange={handleChange}
                      className="poppins-font"
                      disabled={categories.length === 0}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category._id} value={category._id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {categories.length === 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        No categories available. Please select a different
                        category type or create categories first.
                      </Typography>
                    )}
                  </FormControl>

                  <FormControl fullWidth required>
                    <InputLabel className="poppins-font">Status *</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      label="Status *"
                      onChange={handleChange}
                      className="poppins-font"
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                      <MenuItem value="deleted">Deleted</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </StyledFormSection>
            </Box>

            {/* Second Row: Alt Tags & Description (Full width) */}
            <StyledFormSection>
              <Typography variant="h6" className="poppins-font" gutterBottom>
                Alt Tags & Description
              </Typography>
              <TextField
                fullWidth
                label="Alt Tags"
                name="altTags"
                value={formData.altTags}
                onChange={handleChange}
                margin="normal"
                helperText="Enter tags separated by commas (e.g., A,B,C)"
                className="poppins-font"
              />
              <Box sx={{ my: 2 }}>
                {formData.altTags
                  ?.split(",")
                  .filter((tag) => tag?.trim())
                  .map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag.trim()}
                      onDelete={() => {
                        const newTags = formData.altTags
                          .split(",")
                          .filter((_, i) => i !== index)
                          .join(",");
                        setFormData((prev) => ({
                          ...prev,
                          altTags: newTags,
                        }));
                      }}
                      sx={{ m: 0.5 }}
                      className="poppins-font"
                    />
                  ))}
              </Box>
            </StyledFormSection>

            {/* Third Row: SEO Settings (Full width) */}
            <StyledFormSection>
              <Typography className="poppins-font" variant="h6" gutterBottom>
                SEO Settings
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 3,
                  }}
                >
                  <TextField
                    fullWidth
                    label="SEO Title"
                    name="seoTitle" // Changed from metaTitle
                    value={formData.seoTitle}
                    onChange={handleChange}
                    className="poppins-font"
                  />
                  <TextField
                    fullWidth
                    label="SEO Keywords"
                    name="seoKeywords" // Changed from metaKeywords
                    value={formData.seoKeywords}
                    onChange={handleChange}
                    helperText="Enter keywords separated by commas"
                    className="poppins-font"
                  />
                </Box>
                <TextField
                  fullWidth
                  label="SEO Description"
                  name="seoDescription" // Changed from metaDescription
                  value={formData.seoDescription}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  className="poppins-font"
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                {formData.seoKeywords // Changed from metaKeywords
                  ?.split(",")
                  .filter((keyword) => keyword?.trim())
                  .map((keyword, index) => (
                    <Chip
                      key={index}
                      label={keyword.trim()}
                      onDelete={() => {
                        const newKeywords = formData.seoKeywords // Changed from metaKeywords
                          .split(",")
                          .filter((_, i) => i !== index)
                          .join(",");
                        setFormData((prev) => ({
                          ...prev,
                          seoKeywords: newKeywords, // Changed from metaKeywords
                        }));
                      }}
                      sx={{ m: 0.5 }}
                      className="poppins-font"
                    />
                  ))}
              </Box>
            </StyledFormSection>
          </Box>

          {/* Form Actions */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              left: 0,
              right: 0,
              py: 2,
              mt: 3,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              zIndex: 1000,
            }}
          >
            <Button onClick={onClose} variant="outlined" sx={{ minWidth: 120 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ minWidth: 120 }}
              className="poppins-font"
            >
              {loading ? "Saving..." : editData ? "Update" : "Submit"}
            </Button>
          </Box>
        </form>
      </Box>

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </StyledFormContainer>
  );
};

export default Form;
